import { useState, type FormEvent } from 'react';
import type { ClientConfig } from '@ferro/config';
import {
  CreateIntakeInviteError,
  createIntakeInvite,
  readSession,
  type CreateIntakeInviteResponse,
} from '../../lib/api.js';

// D2.2.1 — Platform-admin page for minting a new intake invite.
//
// A staff user (role=platform_admin) enters the prospect's email plus
// optional context (first name, company display name, inviter sign-off)
// and the server mints a `TenantIntake` row and emails the prospect a
// magic link to the 10-step portal. On success the page shows the
// invite URL so the admin can copy/paste into Slack if the email hasn't
// arrived yet.

type IntakeNewProps = {
  config: ClientConfig;
};

type FieldErrors = Record<string, string>;

export function IntakeNew({ config }: IntakeNewProps): JSX.Element {
  const [email, setEmail] = useState('');
  const [companyDisplayName, setCompanyDisplayName] = useState('');
  const [prospectFirstName, setProspectFirstName] = useState('');
  const [inviterName, setInviterName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<CreateIntakeInviteResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    setResult(null);

    const { token } = readSession();
    if (!token) {
      setError('Your session has expired. Please sign in again.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await createIntakeInvite(config.deployment.apiUrl, token, {
        email: email.trim(),
        prospectFirstName: prospectFirstName.trim() || undefined,
        companyDisplayName: companyDisplayName.trim() || undefined,
        inviterName: inviterName.trim() || undefined,
      });
      setResult(res);
      // Leave the form populated so the admin can tweak + resend if
      // needed — they can clear manually.
    } catch (err) {
      if (err instanceof CreateIntakeInviteError) {
        if (err.kind === 'validation' && err.body?.issues) {
          const next: FieldErrors = {};
          for (const issue of err.body.issues) {
            next[issue.path] = issue.message;
          }
          setFieldErrors(next);
          setError('Please fix the highlighted fields.');
        } else {
          setError(err.message);
        }
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Unexpected error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = (): void => {
    setEmail('');
    setCompanyDisplayName('');
    setProspectFirstName('');
    setInviterName('');
    setResult(null);
    setError(null);
    setFieldErrors({});
  };

  return (
    <section className="ferro-admin-page">
      <header>
        <h1 className="ferro-section__title">Invite a new tenant</h1>
        <p className="ferro-muted">
          Send the prospect a magic link to the 10-step intake portal.
          Links expire in 21 days.
        </p>
      </header>

      <form
        className="ferro-form"
        onSubmit={handleSubmit}
        noValidate
        aria-label="Create intake invite"
      >
        <label className="ferro-field">
          <span className="ferro-field__label">Prospect email</span>
          <input
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder="ted@happytailhappydog.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="ferro-field__input"
            aria-invalid={fieldErrors.email ? true : undefined}
          />
          {fieldErrors.email ? (
            <span className="ferro-field__error" role="alert">
              {fieldErrors.email}
            </span>
          ) : null}
        </label>

        <label className="ferro-field">
          <span className="ferro-field__label">Company display name</span>
          <input
            type="text"
            name="companyDisplayName"
            placeholder="Happy Tail Happy Dog"
            value={companyDisplayName}
            onChange={(e) => setCompanyDisplayName(e.target.value)}
            className="ferro-field__input"
          />
          <span className="ferro-field__hint">
            Used in the email subject and opening sentence.
          </span>
        </label>

        <label className="ferro-field">
          <span className="ferro-field__label">Prospect first name</span>
          <input
            type="text"
            name="prospectFirstName"
            autoComplete="given-name"
            placeholder="Ted"
            value={prospectFirstName}
            onChange={(e) => setProspectFirstName(e.target.value)}
            className="ferro-field__input"
          />
          <span className="ferro-field__hint">
            Optional — used for the greeting. Falls back to "Hello,".
          </span>
        </label>

        <label className="ferro-field">
          <span className="ferro-field__label">Inviter sign-off</span>
          <input
            type="text"
            name="inviterName"
            placeholder="Johnny from Ferro"
            value={inviterName}
            onChange={(e) => setInviterName(e.target.value)}
            className="ferro-field__input"
          />
          <span className="ferro-field__hint">
            Optional — shown in the email sign-off. Defaults to "Johnny at
            Animal Lovers".
          </span>
        </label>

        {error ? (
          <p className="ferro-form__error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="ferro-form__actions">
          <button
            type="submit"
            className="ferro-btn ferro-btn--primary"
            disabled={submitting}
          >
            {submitting ? 'Sending…' : 'Send intake invite'}
          </button>
          <button
            type="button"
            className="ferro-btn"
            onClick={handleReset}
            disabled={submitting}
          >
            Reset
          </button>
        </div>
      </form>

      {result ? (
        <section
          className="ferro-card ferro-card--success"
          role="status"
          aria-live="polite"
        >
          <h2 className="ferro-card__title">Invite sent</h2>
          <p>
            We emailed <strong>{result.inviteEmail}</strong> a link to the
            intake portal.
          </p>
          <dl className="ferro-dl">
            <dt>Magic link</dt>
            <dd>
              <a
                href={result.inviteUrl}
                target="_blank"
                rel="noreferrer noopener"
              >
                {result.inviteUrl}
              </a>
            </dd>
            <dt>Expires</dt>
            <dd>{new Date(result.inviteExpiresAt).toLocaleString()}</dd>
            <dt>Intake id</dt>
            <dd>
              <code>{result.intakeId}</code>
            </dd>
          </dl>
        </section>
      ) : null}
    </section>
  );
}
